import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import PrintIcon from '@mui/icons-material/Print'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useMemo, useState } from 'react'
import {
  type QualitySampling,
  useBulkDeleteQualitySamplingMutation,
  useGetQualitySamplingsQuery,
} from '../../store/api/qualityApi'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import SamplingFormDialog from './SamplingFormDialog'
import SamplingDetailsDialog from './SamplingDetailsDialog'
import SamplingEditDoDialog from './SamplingEditDoDialog'
import SamplingEditPsResultDialog from './SamplingEditPsResultDialog'
import SamplingPssLabelDialog from './SamplingPssLabelDialog'
import {
  DataframeBody,
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  useRowMultiSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type FilterKind = 'search'

/** Freeze the Sample No identifier column when scrolling horizontally (skill /table-style §7.6).
 *  Sample No is the row identifier (SAM-xxxxxx) — pinned so it stays visible while scrolling
 *  the wide datasheet. Column display order is unchanged (Sample Type stays first). */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['sampleNo'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { sampleNo: 120 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { sampleNo: 100 }

/** TablePagination «All» maps to this pageSize (single request). */
const ALL_ROWS_PAGE_SIZE = 1_000_000

/** Width (px) of the leftmost checkbox select column (in gộp nhiều mẫu PSS). */
const SELECT_COL_WIDTH = 44
/** Cap mềm số mẫu tick chọn để in 1 lần (khớp backend MAX_SAMPLES_PER_PRINT). */
const MAX_PRINT_SAMPLES = 20

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof QualitySampling
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

/**
 * Column order + captions follow Access `frmQualityQrySampling` datasheet
 * (control declaration order + Label.Caption). Numbers `Standard`/2dp right-aligned,
 * dates `d-mmm-yyyy`.
 */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'sampleType', label: 'Sample Type', filter: 'checkboxes' },
  { key: 'sampleNo', label: 'Sample No' },
  { key: 'psDate', label: 'PS Date', kind: 'date' },
  { key: 'psSentDate', label: 'PS Sent Date', kind: 'date' },
  { key: 'resultReceiptDate', label: 'Result Receipt Date', kind: 'date' },
  { key: 'result', label: 'Result', filter: 'checkboxes' },
  { key: 'rejectReason', label: 'Reject Reason', wrap: true, width: 200 },
  { key: 'tlcContractRef', label: 'TLC Contract Ref' },
  { key: 'clientLotId', label: 'Client Lot ID' },
  { key: 'buyersRef', label: 'Buyers Ref' },
  { key: 'txtBuyer', label: 'Buyer' },
  { key: 'consignee', label: 'Consignee Name' },
  { key: 'deliveryTo', label: 'Delivery To', kind: 'date' },
  { key: 'quality', label: 'Sale - Quality', filter: 'checkboxes' },
  { key: 'grade', label: 'Sample - Grade' },
  { key: 'certification', label: 'Certification', filter: 'checkboxes' },
  { key: 'warehouseCode', label: 'Warehouse Code', filter: 'checkboxes' },
  { key: 'whSamplingName', label: 'Warehouse Name' },
  { key: 'origin', label: 'Origin', filter: 'checkboxes' },
  { key: 'lotId', label: 'Lot ID' },
  { key: 'txtfullLotNo', label: 'Full Lot No' },
  { key: 'lotSizeMt', label: 'Lot Size Mt', kind: 'number', align: 'right' },
  { key: 'weightPerContMt', label: 'Weight Per Cont Mt', kind: 'number', align: 'right' },
  { key: 'licenseCertNo', label: 'License Cert No' },
  { key: 'certUnit', label: 'Cert Unit' },
  { key: 'inspectionBy', label: 'Inspection By' },
  { key: 'sealNo', label: 'Seal No' },
  { key: 'note', label: 'Note', wrap: true, width: 200 },
  { key: 'clientSampleRef', label: 'Client Sample Ref', wrap: true, width: 200 },
  { key: 'note2', label: 'Note2', wrap: true, width: 200 },
  { key: 'doNo', label: 'DO No' },
  { key: 'qtyPreshipmentClient', label: 'Qty Preshipment Client', kind: 'number', align: 'right' },
  { key: 'qtyPreshipmentSupervisor', label: 'Qty Preshipment Supervisor', kind: 'number', align: 'right' },
  { key: 'cost', label: 'Cost', kind: 'number', align: 'right' },
  { key: 'qtyAftershipmentClient', label: 'Qty Aftershipment Client', kind: 'number', align: 'right' },
  { key: 'qtyAftershipmentSupervisor', label: 'Qty Aftershipment Supervisor', kind: 'number', align: 'right' },
  { key: 'receiver', label: 'Receiver' },
  { key: 'receivingAuthority', label: 'Receiving Authority' },
  { key: 'loadingType', label: 'Loading Type', filter: 'checkboxes' },
  { key: 'loadingDate', label: 'Loading Date', kind: 'date' },
  { key: 'fumigationNote', label: 'Fumigation Note', wrap: true, width: 200 },
  { key: 'alertTxt', label: 'Warning Text', wrap: true, width: 200 },
  { key: 'sampleStatus', label: 'Sample Status', filter: 'checkboxes' },
  { key: 'statusDate', label: 'Sample Status - Date', kind: 'date' },
  { key: 'statusReason', label: 'Sample Status - Reason', wrap: true, width: 200 },
  { key: 'createdDate', label: 'Created Date', kind: 'datetime' },
  { key: 'createdBy', label: 'Created By' },
  { key: 'updatedDate', label: 'Updated Date', kind: 'datetime' },
  { key: 'updatedBy', label: 'Updated By' },
  { key: 'isStatus', label: 'Is Status', kind: 'number', align: 'right' },
  { key: 'id', label: 'Id', kind: 'number', align: 'right' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: QualitySampling[], key: keyof QualitySampling): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

interface SamplingDataListViewProps {
  /** Quality Control fiscal-year date range (ps_date filter), from QualityControlPage. */
  dateFrom?: string
  dateTo?: string
}

export default function SamplingDataListView({ dateFrom = '', dateTo = '' }: SamplingDataListViewProps) {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  // Focus row (click dòng) → New/Edit/Copy/Delete/Details như cũ.
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [confirmDelete, setConfirmDelete] = useState<{ ids: number[]; labels: string[] } | null>(null)
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy'; samplingId: number | null } | null>(null)
  const [detailsId, setDetailsId] = useState<number | null>(null)
  const [editDoOpen, setEditDoOpen] = useState(false)
  const [editPsRef, setEditPsRef] = useState<string | null>(null)
  const [printState, setPrintState] = useState<{ open: boolean; sampleNos: string[] }>({ open: false, sampleNos: [] })

  const [bulkDeleteSampling, { isLoading: deleting }] = useBulkDeleteQualitySamplingMutation()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('quality-sampling.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof QualitySampling> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof QualitySampling
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'quality',
    prefKey: 'columns',
    subKey: 'sampling-list',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'quality-sampling.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetQualitySamplingsQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
    },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => list?.items ?? [], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number' && col.key !== 'id' && col.key !== 'isStatus')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'ID: —'
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const r of rows) {
      const id = Number(r.id)
      if (!Number.isFinite(id)) continue
      if (id < min) min = id
      if (id > max) max = id
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'ID: —'
    return `ID: ${min}–${max}`
  }, [rows])
  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof QualitySampling) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<QualitySampling | null>(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  )

  // ── Multi-select (checkbox) cho in gộp PSS — hook chung useRowMultiSelection.
  // Tick checkbox → in gộp NHIỀU mẫu PSS (độc lập với focus row). Select-all
  // theo `rows` (đang hiển thị); target resolve theo `baseRows` để giữ cả dòng
  // đã tick nhưng đang bị lọc ẩn.
  const {
    selectedIds,
    setSelectedIds,
    selectedRows: selectedRowsForPrint,
    toggle: toggleId,
    toggleAll: toggleSelectAll,
    allVisibleSelected,
    someVisibleSelected,
  } = useRowMultiSelection(rows, (r) => r.id, baseRows)
  // Target cho Print/Delete: các dòng đã tick; không tick thì dòng đang focus.
  const actionTargetRows = useMemo(
    () => (selectedRowsForPrint.length > 0 ? selectedRowsForPrint : selectedRow ? [selectedRow] : []),
    [selectedRowsForPrint, selectedRow],
  )
  // Tick > 1 dòng → các action chỉ dành cho 1 bản ghi (Edit/Copy/Edit PS Result/
  // Sample Details) bị vô hiệu vì target mơ hồ. Print/Delete/Edit DO không ảnh hưởng.
  const multiTicked = selectedIds.size > 1
  // "1 bản ghi đích" cho các action single: TICK ĐÚNG 1 DÒNG ⇔ focus 1 dòng.
  // Tick >1 → null (disable). Không tick → dòng đang focus (click).
  const singleTargetRow = useMemo<QualitySampling | null>(
    () =>
      selectedRowsForPrint.length > 1
        ? null
        : selectedRowsForPrint.length === 1
          ? selectedRowsForPrint[0]
          : selectedRow,
    [selectedRowsForPrint, selectedRow],
  )

  // Access main-form handlers (frmQualitySamplingSubForm). New/Edit/Copy →
  // frmQualityQrySamplingAddQC; Sample Details → frmQualityQrySamplingAddInfo;
  // Edit DO → frmQualityQrySampling_EditMany_Add; Edit PS Result →
  // frmQualityQrySampling_EditManyResult_Add. Dialogs are converted in the
  // fan-out step (/convert-form) — surfaced as notices until then.
  // Trả về dòng đích cho action single (tick 1 hoặc focus 1); cảnh báo nếu không
  // xác định được (0 chọn, hoặc tick >1 → mơ hồ).
  const requireSingle = (): QualitySampling | null => {
    if (!singleTargetRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return singleTargetRow
  }

  const handleNew = () => {
    if (!canEdit) return
    setFormState({ mode: 'new', samplingId: null })
  }
  const handleEdit = () => {
    if (!canEdit) return
    const row = requireSingle()
    if (!row) return
    setFormState({ mode: 'edit', samplingId: row.id })
  }
  const handleCopy = () => {
    if (!canEdit) return
    const row = requireSingle()
    if (!row) return
    setFormState({ mode: 'copy', samplingId: row.id })
  }
  const handleEditDo = () => {
    if (!canEdit) return
    setEditDoOpen(true)
  }
  const handleEditPsResult = () => {
    if (!canEdit) return
    const row = requireSingle()
    if (!row) return
    if (!row.tlcContractRef) {
      snack.warning('This record has no TLC Contract Ref.')
      return
    }
    setEditPsRef(row.tlcContractRef)
  }
  const handleSampleDetails = () => {
    // Editable form; view-only users still open it (Save disabled via canEdit).
    const row = requireSingle()
    if (!row) return
    setDetailsId(row.id)
  }

  const handlePrint = () => {
    // Tick nhiều dòng → in các dòng tick (in gộp); không tick → in dòng focus.
    const targets = actionTargetRows
    if (targets.length === 0) {
      snack.warning('Please choose a record!')
      return
    }
    if (targets.length > MAX_PRINT_SAMPLES) {
      snack.warning('Too many records to Print!')
      return
    }
    const sampleNos = Array.from(new Set(targets.map((r) => r.sampleNo ?? '').filter(Boolean)))
    if (sampleNos.length === 0) {
      snack.warning('Selected record(s) have no Sample No.')
      return
    }
    setPrintState({ open: true, sampleNos })
  }

  const handleDeleteClick = () => {
    if (!canEdit) return
    const targets = actionTargetRows
    if (targets.length === 0) {
      snack.warning('Please choose a record!')
      return
    }
    if (targets.length > 10) {
      snack.warning('Cannot delete more than 10 records at once. Please tick 10 or fewer.')
      return
    }
    setConfirmDelete({
      ids: targets.map((r) => r.id),
      labels: targets.map((r) => r.sampleNo ?? String(r.id)),
    })
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      const res = await bulkDeleteSampling(confirmDelete.ids).unwrap()
      snack.success(res.message || `Deleted ${confirmDelete.ids.length} record(s)`)
      // Xoá xong → bỏ tick + bỏ focus các dòng đã xoá.
      setSelectedIds((prev) => {
        const next = new Set(prev)
        confirmDelete.ids.forEach((id) => next.delete(id))
        return next
      })
      if (selectedId != null && confirmDelete.ids.includes(selectedId)) setSelectedId(null)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete sampling failed'))
    }
  }

  const cellValue = (row: QualitySampling, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            {/* Toolbar order = Access frmQualitySamplingSubForm (Left):
                New · Edit · Copy · Edit DO · Edit PS Result · Sample Details · Delete.
                All hidden when screenAccess<>1 → disabled when !canEdit. */}
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleNew}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!singleTargetRow || !canEdit}
              onClick={handleEdit}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title={multiTicked ? 'Bỏ bớt tick — Edit chỉ cho 1 bản ghi' : 'Edit'}
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!singleTargetRow || !canEdit}
              onClick={handleCopy}
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              title={multiTicked ? 'Bỏ bớt tick — Copy chỉ cho 1 bản ghi' : 'Copy'}
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleEditDo}
              startIcon={<LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit DO"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Edit&nbsp;</Box>DO
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!singleTargetRow || !canEdit}
              onClick={handleEditPsResult}
              startIcon={<PlaylistAddCheckIcon sx={{ fontSize: 16 }} />}
              title={multiTicked ? 'Bỏ bớt tick — Edit PS Result chỉ cho 1 bản ghi' : 'Edit PS Result'}
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Edit&nbsp;</Box>PS Result
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              sx={actionToolbarBtnSx}
              disabled={!singleTargetRow || !canEdit}
              onClick={handleSampleDetails}
              startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
              title={multiTicked ? 'Bỏ bớt tick — Sample Details chỉ cho 1 bản ghi' : 'Sample Details'}
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Sample&nbsp;</Box>Details
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={selectedIds.size === 0 && !selectedRow}
              onClick={handlePrint}
              startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              title="Pr-PSS (tick multiple samples to print together)"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Pr-PSS</Box>
              {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={(selectedIds.size === 0 && !selectedRow) || !canEdit}
              onClick={handleDeleteClick}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              title="Delete (tick multiple rows to delete at once)"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Sampling Data.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              {idRangeText}
            </Typography>
            {selectedIds.size > 0 ? (
              <Typography
                variant="caption"
                color="primary"
                onClick={() => setSelectedIds(new Set())}
                title="Clear selection"
                sx={{ fontWeight: 700, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', cursor: 'pointer' }}
              >
                Selected: {selectedIds.size} ✕
              </Typography>
            ) : null}
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Sample no, contract, lot, buyer..."
              label="Search sampling data"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={(popoverKind === 'none' ? 'text' : popoverKind)}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Sampling Data"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No sampling record found"
              description="Try adjusting the date range or the table filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    className="app-table-header-cell"
                    style={{ position: 'sticky', top: 0, left: 0, zIndex: 101, width: SELECT_COL_WIDTH, minWidth: SELECT_COL_WIDTH }}
                  >
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={!allVisibleSelected && someVisibleSelected}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = SELECT_COL_WIDTH
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  <TableCell className="app-sticky-col" padding="checkbox" style={{ width: SELECT_COL_WIDTH }} sx={{ bgcolor: 'background.paper', borderTop: (t) => `1px solid ${t.palette.divider}` }} />
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w, ...(isFrozen ? { left: SELECT_COL_WIDTH } : {}) } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: QualitySampling) => (
                <>
                  <TableCell
                    padding="checkbox"
                    className="app-sticky-col"
                    style={{ width: SELECT_COL_WIDTH, minWidth: SELECT_COL_WIDTH }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox size="small" checked={selectedIds.has(row.id)} onChange={() => toggleId(row.id)} />
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const val = cellValue(row, col)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const wrap = col.wrap || getWidth(key) != null
                    const frozenStyle: CSSProperties = isFrozen ? { left: SELECT_COL_WIDTH } : {}
                    if (wrap) {
                      return (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          className={isFrozen ? 'app-sticky-col-2' : undefined}
                          style={w ? { width: w, minWidth: w, maxWidth: w, ...frozenStyle } : frozenStyle}
                          sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                        >
                          <Box component="span" className="app-table-cell-wrap">
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col-2' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w, ...frozenStyle } : frozenStyle}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )}
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && (rowId === selectedId || selectedIds.has(rowId))}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowId == null) return
                        toggleSelect(rowId)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Sampling"
        message={
          confirmDelete
            ? confirmDelete.ids.length === 1
              ? `Do you want to delete = '${confirmDelete.labels[0] ?? ''}'?`
              : `Do you want to delete ${confirmDelete.ids.length} selected records?\n${confirmDelete.labels.join(', ')}`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />

      {formState && (
        <SamplingFormDialog
          open
          mode={formState.mode}
          samplingId={formState.mode === 'new' ? null : formState.samplingId}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}
      <SamplingDetailsDialog
        open={detailsId != null}
        samplingId={detailsId}
        canEdit={canEdit}
        onClose={() => setDetailsId(null)}
        onSaved={() => setSelectedId(null)}
      />
      <SamplingEditDoDialog
        open={editDoOpen}
        canEdit={canEdit}
        onClose={() => setEditDoOpen(false)}
        onSaved={() => setSelectedId(null)}
      />
      <SamplingEditPsResultDialog
        open={editPsRef != null}
        tlcContractRef={editPsRef}
        canEdit={canEdit}
        onClose={() => setEditPsRef(null)}
        onSaved={() => setSelectedId(null)}
      />
      <SamplingPssLabelDialog
        open={printState.open}
        sampleNos={printState.sampleNos}
        onClose={() => setPrintState({ open: false, sampleNos: [] })}
      />
    </Box>
  )
}
